import type { MiseSellerGroupNode, MiseSellerNode } from '@/lib/types/mise/seller';
import type { AppDbUserSettings } from '@/lib/types/mise/user';

/** ユーザー一覧に出す比較先（同名の店舗・店舗グループを見分けられるよう、上位からの階層を持つ） */
export interface ComparisonPath {
  kind: 'seller' | 'seller_group';
  /** 上位の店舗グループから比較先までの名前 */
  path: string[];
}

/** 店舗グループの、最上位から自分までの名前を返す。階層に無いグループは自分の名前だけ */
function sellerGroupPath(
  groupId: string,
  fallbackName: string,
  groupsById: Map<string, MiseSellerGroupNode>,
): string[] {
  const path: string[] = [];
  const visited = new Set<string>();
  let current = groupsById.get(groupId);
  // 親をたどる。データ不整合で循環していても止まるよう、訪れたノードを覚えておく
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    path.unshift(current.name ?? current.id);
    current = current.parent_id ? groupsById.get(current.parent_id) : undefined;
  }
  return path.length > 0 ? path : [fallbackName];
}

/**
 * ユーザーの比較店舗（または比較店舗グループ）を、店舗グループの階層つきで返す。
 * 比較先が設定されていなければ null。
 */
export function buildComparisonPath(
  settings: AppDbUserSettings,
  sellers: MiseSellerNode[],
  groups: MiseSellerGroupNode[],
): ComparisonPath | null {
  const groupsById = new Map(groups.map((g) => [g.id, g]));

  if (settings.comparison_seller_group_id) {
    const id = settings.comparison_seller_group_id;
    return {
      kind: 'seller_group',
      path: sellerGroupPath(id, settings.comparison_seller_group_name ?? id, groupsById),
    };
  }

  if (settings.comparison_seller_id) {
    const id = settings.comparison_seller_id;
    const seller = sellers.find((s) => s.id === id);
    const name = seller?.name ?? settings.comparison_seller_name ?? id;
    const parentPath = seller?.parent_id
      ? sellerGroupPath(seller.parent_id, seller.parent_id, groupsById)
      : [];
    return { kind: 'seller', path: [...parentPath, name] };
  }

  return null;
}
